import { useState, useEffect } from "react";
import { keyedEntries } from "@/lib/listIdentity";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CardHoverPreview } from "@/components/Card3D";
import { Layers, Package, Ticket, Coins, Sparkles, Check, Swords, Shield } from "lucide-react";

const RTEXT = { common: "text-zinc-300", uncommon: "text-emerald-300", rare: "text-sky-300", epic: "text-fuchsia-300", legendary: "text-amber-300", goddess: "text-rose-300" };

function Stepper({ label, sub, price, qty, onDec, onInc, canInc, color, Icon, testid }) {
  return (
    <div data-testid={testid} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${qty > 0 ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10 bg-white/[0.03]"}`}>
      <Icon className="h-4 w-4 shrink-0" style={{ color }} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-white truncate">{label}</div>
        <div className="font-mono text-[9px] text-amber-300/80">{price.toLocaleString()}c {sub ? <>· {sub}</> : null}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button type="button" data-testid={`${testid}-dec`} onClick={onDec} disabled={qty <= 0} className="h-6 w-6 rounded bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 leading-none text-base">−</button>
        <span className="font-mono text-sm w-5 text-center text-white">{qty}</span>
        <button type="button" data-testid={`${testid}-inc`} onClick={onInc} disabled={!canInc} className="h-6 w-6 rounded bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 leading-none text-base">+</button>
      </div>
    </div>
  );
}

export function LootRedeemPanel({ battleId, onClaimed }) {
  const [cat, setCat] = useState(null);
  const [cards, setCards] = useState(0);
  const [gear, setGear] = useState(0);
  const [packs, setPacks] = useState({});
  const [tokens, setTokens] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/arena/battles/${battleId}/loot`).then((r) => setCat(r.data)).catch(() => {});
  }, [battleId]);

  if (result) {
    const total = result.cards.length + result.pack_cards.length + (result.gear?.length || 0) + result.tokens.reduce((a, t) => a + t.qty, 0);
    return (
      <div className="mt-5 text-center" data-testid="loot-redeem-result">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-300 mb-3 flex items-center justify-center gap-1.5"><Check className="h-4 w-4" /> Loot claimed · {result.spent.toLocaleString()}c spent</div>
        {result.auto_filled > 0 && (
          <div className="text-[10px] text-cyan-300/80 mb-2" data-testid="loot-auto-filled">🎲 {result.auto_filled} reward{result.auto_filled === 1 ? "" : "s"} randomly drafted from your unspent budget.</div>
        )}
        <div className="flex flex-wrap justify-center gap-2 max-h-52 overflow-y-auto">
          {[...result.cards, ...result.pack_cards].map((c, i) => (
            <CardHoverPreview key={c.id} card={c} side="top">
              <div data-testid={`loot-claimed-card-${i}`} className={`relative w-16 aspect-[2.5/3.5] rounded-md overflow-hidden bg-black border border-white/10 cursor-pointer hover:border-cyan-400/60 transition-colors rarity-${c.rarity}`}>
                {c.front_image && <img src={c.front_image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
                <div className="foil-overlay" />
              </div>
            </CardHoverPreview>
          ))}
          {keyedEntries(result.tokens, (t) => t.id || t.name).map(({ item: t, key }) => (
            <div key={key} className="flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[10px] text-cyan-200"><Ticket className="h-3 w-3" /> {t.qty}× {t.name}</div>
          ))}
          {(result.gear || []).map((g, i) => (
            <div key={g.id} data-testid={`loot-claimed-gear-${i}`} className={`flex items-center gap-1 rounded-md border border-orange-400/30 bg-orange-400/10 px-2 py-1 text-[10px] ${RTEXT[g.rarity] || "text-orange-200"}`}><Shield className="h-3 w-3" /> {g.name}</div>
          ))}
        </div>
        {result.refunded_credits > 0 && (
          <div data-testid="loot-refunded-credits" className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] text-amber-200 font-mono">
            <Coins className="h-3.5 w-3.5" /> +{result.refunded_credits.toLocaleString()}c refunded (unspent budget)
          </div>
        )}
        <p className="text-white/40 text-xs mt-3">{total} reward{total === 1 ? "" : "s"} added to your collection.</p>
        <Button onClick={onClaimed} className="mt-4 bg-cyan-400 text-black hover:bg-cyan-300" data-testid="loot-redeem-done">Back to Arena</Button>
      </div>
    );
  }

  if (!cat) return <div className="mt-5 text-white/40 text-sm">Tallying your spoils…</div>;

  const packPrice = (id) => (cat.packs.find((p) => p.id === id)?.price) || 0;
  const tokenPrice = (t) => (cat.tokens.find((x) => x.type === t)?.price) || 0;
  const spent = cards * cat.card_price
    + gear * (cat.gear_price || 0)
    + Object.entries(packs).reduce((a, [id, q]) => a + q * packPrice(id), 0)
    + Object.entries(tokens).reduce((a, [t, q]) => a + q * tokenPrice(t), 0);
  const remaining = cat.budget - spent;
  const setQ = (setter, obj, key, q) => setter({ ...obj, [key]: Math.max(0, q) });

  const redeem = async (auto = false) => {
    try {
      setBusy(true);
      const { data } = await api.post(`/arena/battles/${battleId}/redeem`, auto ? { auto: true } : { cards, gear, packs, tokens });
      setResult(data);
      toast.success(auto ? "Random loot drafted!" : "Loot redeemed!");
    } catch (e) {
      toast.error(apiError(e, "Could not redeem loot"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 text-left" data-testid="loot-redeem-panel">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300">Redeem your loot</span>
        <span className="font-mono text-[10px] text-white/40">{cat.difficulty}</span>
      </div>
      <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 mb-3 flex items-center justify-between">
        <span className="text-[11px] text-white/60">Loot to spend</span>
        <motion.span key={remaining} initial={{ scale: 1.15 }} animate={{ scale: 1 }} className={`font-mono text-lg font-bold flex items-center gap-1 ${remaining < 0 ? "text-rose-400" : "text-amber-300"}`}>
          <Coins className="h-4 w-4" /> {remaining.toLocaleString()}
        </motion.span>
      </div>
      <div className="space-y-1.5 max-h-[34vh] overflow-y-auto pr-1">
        <Stepper testid="loot-card" Icon={Layers} color="#fbbf24" label={`Mint a ${cat.rarity} card`} sub={<span className={RTEXT[cat.rarity]}>{cat.rarity}</span>}
          price={cat.card_price} qty={cards} onDec={() => setCards(Math.max(0, cards - 1))} onInc={() => setCards(cards + 1)} canInc={remaining >= cat.card_price} />
        {cat.gear_price != null && (
          <Stepper testid="loot-gear" Icon={Swords} color="#fb923c" label={`Forge ${cat.rarity} gear`} sub="weapon / armor / trinket"
            price={cat.gear_price} qty={gear} onDec={() => setGear(Math.max(0, gear - 1))} onInc={() => setGear(gear + 1)} canInc={remaining >= cat.gear_price} />
        )}
        {cat.packs.map((p) => {
          const q = packs[p.id] || 0;
          return <Stepper key={p.id} testid={`loot-pack-${p.id}`} Icon={Package} color="#e879f9" label={p.name} sub="opens instantly"
            price={p.price} qty={q} onDec={() => setQ(setPacks, packs, p.id, q - 1)} onInc={() => setQ(setPacks, packs, p.id, q + 1)} canInc={remaining >= p.price} />;
        })}
        {cat.tokens.map((t) => {
          const q = tokens[t.type] || 0;
          return <Stepper key={t.type} testid={`loot-token-${t.type}`} Icon={Ticket} color={t.color || "#22d3ee"} label={t.name} sub="token"
            price={t.price} qty={q} onDec={() => setQ(setTokens, tokens, t.type, q - 1)} onInc={() => setQ(setTokens, tokens, t.type, q + 1)} canInc={remaining >= t.price} />;
        })}
      </div>
      <Button data-testid="loot-redeem-button" onClick={() => redeem(false)} disabled={busy || spent > cat.budget}
        className="w-full mt-3 bg-amber-400 text-black hover:bg-amber-300 font-semibold disabled:opacity-50">
        <Sparkles className="h-4 w-4 mr-1.5" />
        {busy ? "Redeeming…"
          : spent > 0
            ? `Redeem ${spent.toLocaleString()}c${remaining > 0 ? ` · Refund ${remaining.toLocaleString()}c` : ""}`
            : `Skip loot · Refund ${cat.budget.toLocaleString()}c`}
      </Button>
      <button type="button" data-testid="loot-redeem-random" onClick={() => redeem(true)} disabled={busy}
        className="w-full mt-2 text-[11px] text-cyan-300/80 hover:text-cyan-200 underline-offset-2 hover:underline disabled:opacity-40">
        🎲 Surprise me — draft the full {cat.budget.toLocaleString()}c at random
      </button>
      <p className="text-white/40 text-[10px] mt-1.5 text-center">Unspent budget is refunded to your credits automatically — pick only what you want.</p>
    </div>
  );
}
